import { apolloClient } from '@/plugins/apollo'
import { VS_SIGNUP_AVAILABILITY } from '@/api/graphql/user'
import { getVsSignupExceptionKey, isVsSignupAllowed, isVsSignupClosedError, vsSignupRemainingMs } from '@/utils/vs-signup-policy'

const REFRESH_MS = 30000
const MAX_TIMEOUT_MS = 2147483647
const isSignup = route => route.matched.some(record => record.meta.vsSignup)

export default function installVsSignupCutoff (router) {
  let cutoffTimer
  let refreshTimer
  let requestId = 0
  let navigationId = 0
  let approvedRoute
  let approvedDeadline
  let deadline

  function clearTimers () {
    clearTimeout(cutoffTimer)
    clearTimeout(refreshTimer)
  }

  function close () {
    clearTimers()
    requestId++
    if (isSignup(router.currentRoute)) {
      router.replace({ name: 'signup-not-found' }).catch(() => {})
    }
  }

  async function check (route) {
    const exceptionKey = getVsSignupExceptionKey(route)
    const startedAt = performance.now()
    const { data } = await apolloClient.query({
      query: VS_SIGNUP_AVAILABILITY,
      variables: { exceptionKey },
      fetchPolicy: 'no-cache'
    })
    const elapsed = performance.now() - startedAt
    const availability = data.vsSignupAvailability
    return {
      allowed: isVsSignupAllowed(availability, exceptionKey, elapsed),
      deadline: exceptionKey ? null : performance.now() + vsSignupRemainingMs(availability, elapsed)
    }
  }

  function schedule () {
    clearTimers()
    if (deadline !== null) {
      const remaining = deadline - performance.now()
      if (remaining <= 0) return close()
      cutoffTimer = setTimeout(schedule, Math.min(remaining, MAX_TIMEOUT_MS))
    }
    scheduleRefresh()
  }

  function scheduleRefresh () {
    clearTimeout(refreshTimer)
    refreshTimer = setTimeout(refresh, REFRESH_MS)
  }

  async function refresh () {
    const route = router.currentRoute
    if (!isSignup(route)) return
    if (deadline !== null && deadline <= performance.now()) return close()
    const id = ++requestId
    try {
      const result = await check(route)
      if (id !== requestId || router.currentRoute.fullPath !== route.fullPath) return
      if (!result.allowed) return close()
      deadline = result.deadline
      schedule()
    } catch (error) {
      if (id !== requestId || router.currentRoute.fullPath !== route.fullPath) return
      if (isVsSignupClosedError(error) || (deadline !== null && deadline <= performance.now())) return close()
      // A temporary outage does not revoke already-validated access. Keep the
      // existing public cutoff timer running and retry without extending it.
      scheduleRefresh()
    }
  }

  router.beforeEach(async (to, from, next) => {
    const id = ++navigationId
    if (!isSignup(to)) return next()
    // GTM loaded on another page could observe SPA navigation to the secret URL.
    // Enter this context through a fresh document whose HTML suppresses GTM.
    if (getVsSignupExceptionKey(to) && !/^\/special-signup\//i.test(window.location.pathname)) {
      window.location.replace(router.resolve(to).href)
      return next(false)
    }
    requestId++
    try {
      const result = await check(to)
      if (id !== navigationId) return next(false)
      if (!result.allowed) return next({ name: 'signup-not-found', replace: true })
      approvedRoute = to.fullPath
      approvedDeadline = result.deadline
      next()
    } catch (_) {
      if (id !== navigationId) return next(false)
      next({ name: 'signup-not-found', replace: true })
    }
  })

  router.afterEach(to => {
    clearTimers()
    requestId++
    if (isSignup(to) && approvedRoute === to.fullPath) {
      deadline = approvedDeadline
      schedule()
    }
  })
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) refresh()
  })
  window.addEventListener('pageshow', refresh)
}
